import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Selection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { EVIDENCE_RUN_PROXY_ATTRIBUTE } from "../../evidence/document-evidence-dom";
import {
  collectEvidenceRuns,
  collectHoistableEvidenceRuns,
  type HoistableEvidenceRun,
  readEvidenceLabelFromAttrs,
} from "../../evidence/evidence-runs";

/**
 * The hoists are cached so a selection-only transaction can recompute which of them the caret
 * is inside without re-walking the document; `decorations` is what the view actually reads.
 */
export interface DocumentEvidenceRunsState {
  hoistable: HoistableEvidenceRun[];
  /** `blockFrom` of every hoist currently revealed because the selection is inside it. */
  revealed: number[];
  decorations: DecorationSet;
}

export const documentEvidenceRunsPluginKey = new PluginKey<DocumentEvidenceRunsState>(
  "documentEvidenceRuns"
);

/**
 * Where a citation sits inside a collapsed run. Carried on the decoration *spec* rather
 * than as decoration attributes: ProseMirror applies decoration attributes to the node
 * view's outer element, which for a Tiptap React node view is the `node-*` wrapper rather
 * than the chip that owns the `data-evidence-*` DOM contract. Reading the spec inside the
 * node view keeps every evidence attribute on one element.
 */
export interface EvidenceRunPlacement {
  size: number;
  isHead: boolean;
}

interface EvidenceRunDecorationSpec {
  evidenceRunSize: number;
  evidenceRunHead: boolean;
}

function isEvidenceRunSpec(spec: unknown): spec is EvidenceRunDecorationSpec {
  return (
    typeof spec === "object" &&
    spec !== null &&
    typeof (spec as EvidenceRunDecorationSpec).evidenceRunSize === "number"
  );
}

/**
 * The run placement a node view should render, or `null` when the node stands alone.
 */
export function readEvidenceRunPlacement(
  decorations: readonly Decoration[]
): EvidenceRunPlacement | null {
  for (const decoration of decorations) {
    if (isEvidenceRunSpec(decoration.spec)) {
      return { size: decoration.spec.evidenceRunSize, isHead: decoration.spec.evidenceRunHead };
    }
  }
  return null;
}

/**
 * Hides the citation-only paragraph a hoisted run was authored in; styled in `index.css`. Only
 * while the selection is outside it — see `collectRevealedHoists`.
 */
const HOISTED_EVIDENCE_PARAGRAPH_CLASS = "evidence-hoisted-paragraph";

/**
 * The markup `lucide-react` renders for `FileText` at the compact marker's size. The proxy
 * marker is plain DOM built by this plugin rather than a React node view, so the icon is
 * inlined here; keep it in step with `ReferenceChip`'s `icon` rendering.
 */
const FILE_TEXT_ICON_MARKUP =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" ' +
  'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
  'stroke-linejoin="round" class="lucide lucide-file-text size-3" aria-hidden="true">' +
  '<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/>' +
  '<path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M10 9H8"/><path d="M16 13H8"/>' +
  '<path d="M16 17H8"/></svg>';

const PROXY_MARKER_CLASS_NAMES = [
  "reference-chip",
  "reference-chip--evidence",
  "reference-chip--evidence-compact",
  "reference-chip--evidence-proxy",
].join(" ");

function setOptionalAttribute(element: HTMLElement, name: string, value: unknown): void {
  if (value === null || value === undefined || value === "") return;
  element.setAttribute(name, String(value));
}

/**
 * The stand-in marker drawn at the end of the preceding line. It carries the head
 * citation's locator so the interaction layer treats it exactly like a real marker, plus
 * the run's own position so a click can list every citation the hidden paragraph holds.
 */
function buildProxyMarker(hoisted: HoistableEvidenceRun): HTMLElement {
  const head = hoisted.run.nodes[0];
  const marker = document.createElement("span");
  marker.className = PROXY_MARKER_CLASS_NAMES;
  marker.setAttribute("data-type", "documentEvidence");
  marker.setAttribute("data-reference-kind", "evidence");
  marker.setAttribute(EVIDENCE_RUN_PROXY_ATTRIBUTE, "true");
  marker.setAttribute("data-evidence-run-pos", String(hoisted.run.positions[0] ?? -1));
  // Matching the real head marker: the count badge is keyed off the attribute's presence,
  // so a run of one must not carry it.
  if (hoisted.run.positions.length > 1) {
    marker.setAttribute("data-evidence-run-size", String(hoisted.run.positions.length));
  }
  if (head) {
    setOptionalAttribute(marker, "data-artifact-id", head.attrs.artifactId);
    setOptionalAttribute(marker, "data-processing-revision-id", head.attrs.processingRevisionId);
    setOptionalAttribute(marker, "data-block-id", head.attrs.blockId);
    setOptionalAttribute(marker, "data-start-char", head.attrs.startChar);
    setOptionalAttribute(marker, "data-end-char", head.attrs.endChar);
    const label = readEvidenceLabelFromAttrs(head.attrs);
    // Both are replaced by the resolved document name once the interaction layer copies it
    // over from the real marker; until then the stored label stands in.
    marker.setAttribute("data-evidence-label", label);
    marker.setAttribute("aria-label", label);
  }
  marker.setAttribute("contenteditable", "false");

  const icon = document.createElement("span");
  icon.className = "reference-chip__icon";
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML = FILE_TEXT_ICON_MARKUP;
  const label = document.createElement("span");
  label.className = "reference-chip__label";
  marker.append(icon, label);

  return marker;
}

/**
 * Stable across rebuilds so an unrelated edit does not re-create the widget's DOM (and with
 * it the availability state the interaction layer syncs onto it). Two citation paragraphs
 * citing the same passage are disambiguated by document order.
 */
function buildProxyKey(hoisted: HoistableEvidenceRun, used: Map<string, number>): string {
  const attrs = hoisted.run.nodes[0]?.attrs ?? {};
  const base = [
    "evidence-proxy",
    attrs.artifactId ?? "",
    attrs.processingRevisionId ?? "",
    attrs.blockId ?? "",
    attrs.startChar ?? "",
    attrs.endChar ?? "",
  ].join("|");
  const seen = used.get(base) ?? 0;
  used.set(base, seen + 1);
  return seen === 0 ? base : `${base}#${seen}`;
}

/**
 * The hoists the caret currently sits inside, by `blockFrom`, in document order. A hidden
 * paragraph the selection has moved into would be a caret in invisible DOM, so it is shown
 * where it is stored for as long as the selection stays there (LAT-926).
 */
function collectRevealedHoists(
  hoistable: readonly HoistableEvidenceRun[],
  selection: Selection
): number[] {
  return hoistable
    .filter((hoisted) => selection.from < hoisted.blockTo && selection.to > hoisted.blockFrom)
    .map((hoisted) => hoisted.blockFrom);
}

function isSameRevealed(left: readonly number[], right: readonly number[]): boolean {
  return left.length === right.length && left.every((pos, index) => pos === right[index]);
}

/**
 * Adjacency is a property of the document rather than of any single node, so it is derived
 * here and handed to the node views as decorations. The stored ProseMirror JSON is never
 * touched — this is rendering only.
 */
function buildDecorations(
  doc: ProseMirrorNode,
  hoistable: readonly HoistableEvidenceRun[],
  revealed: readonly number[]
): DecorationSet {
  const decorations: Decoration[] = [];
  const proxyKeys = new Map<string, number>();

  for (const hoisted of hoistable) {
    // Consumed for every hoist, revealed or not: the duplicate counter must follow document
    // order alone, or revealing one of two same-passage citations would shift the other's key
    // and recreate its widget DOM (losing the state the interaction layer synced onto it).
    const proxyKey = buildProxyKey(hoisted, proxyKeys);
    // A revealed hoist renders exactly as it is stored: no hidden paragraph and no proxy, so
    // the caret inside it is visible and the marker is never drawn twice.
    if (revealed.includes(hoisted.blockFrom)) continue;
    decorations.push(
      // The citations stay in the DOM inside the hidden paragraph, so clipboard, undo,
      // selection and the stored JSON are all unaffected by the hoist.
      Decoration.node(hoisted.blockFrom, hoisted.blockTo, {
        class: HOISTED_EVIDENCE_PARAGRAPH_CLASS,
      }),
      Decoration.widget(hoisted.hostPos, () => buildProxyMarker(hoisted), {
        // After the last character of the host line, never before it.
        side: 1,
        key: proxyKey,
      })
    );
  }

  for (const run of collectEvidenceRuns(doc)) {
    if (run.positions.length < 2) continue;

    run.positions.forEach((pos, index) => {
      const node = run.nodes[index];
      if (!node) return;
      decorations.push(
        // Flat primitives: decoration equality shallow-compares the spec, so a rebuild
        // that produces the same placement does not churn the node view.
        Decoration.node(
          pos,
          pos + node.nodeSize,
          {},
          { evidenceRunSize: run.positions.length, evidenceRunHead: index === 0 }
        )
      );
    });
  }

  return DecorationSet.create(doc, decorations);
}

function buildPluginState(doc: ProseMirrorNode, selection: Selection): DocumentEvidenceRunsState {
  const hoistable = collectHoistableEvidenceRuns(doc);
  const revealed = collectRevealedHoists(hoistable, selection);
  return { hoistable, revealed, decorations: buildDecorations(doc, hoistable, revealed) };
}

export function documentEvidenceRunsPlugin(): Plugin<DocumentEvidenceRunsState> {
  return new Plugin<DocumentEvidenceRunsState>({
    key: documentEvidenceRunsPluginKey,
    state: {
      init: (_config, state) => buildPluginState(state.doc, state.selection),
      apply: (transaction, previous, _oldState, newState) => {
        if (transaction.docChanged) return buildPluginState(newState.doc, newState.selection);
        // Runs only change when the document does, so a selection-only transaction reuses the
        // cached hoists instead of re-walking the document; only a change to which of them the
        // caret is inside is worth rebuilding the decorations for.
        const revealed = collectRevealedHoists(previous.hoistable, newState.selection);
        if (isSameRevealed(revealed, previous.revealed)) return previous;
        return {
          hoistable: previous.hoistable,
          revealed,
          decorations: buildDecorations(newState.doc, previous.hoistable, revealed),
        };
      },
    },
    props: {
      decorations(state) {
        return documentEvidenceRunsPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
      },
    },
  });
}
